import { useMutation } from '@tanstack/react-query';
import type { APPLICANT_ATTRIBUTION_BOTH_TYPE } from 'cpool-constants';
import { AGGREGATE_NAMES } from 'cpool-constants';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import type { Action, Credit, LoanType } from '../../types';
import { getCommandName } from '../utils/getCommandName';
import { APICommunicator } from '~/api/APICommunicator';
import { setQueryPosition } from '~/store/queryPositionStore';
import { queryKeys } from '~/utils/queryKeys';

export type CreditMutationPayload = Partial<Credit> & {
    gehoertZuAntragsteller: APPLICANT_ATTRIBUTION_BOTH_TYPE;
};

export const useExistingLoanActions = ({ loanRequestId }: { loanRequestId: string }) => {
    const { t } = useTranslation();

    const mutationGenerator =
        (action: Action) =>
        ({ loanType, payload }: { loanType: LoanType; payload: CreditMutationPayload }) => {
            const payloadToSend = { ...payload };
            if ('hide' in payloadToSend) {
                delete payloadToSend.hide;
            }
            return APICommunicator.sendCommand({
                aggregateId: loanRequestId,
                aggregateName: AGGREGATE_NAMES.LOAN_REQUEST,
                commandName: getCommandName({ action, loanType }),
                payload: payloadToSend,
            });
        };

    const changeMutation = useMutation({
        mutationFn: mutationGenerator('change'),
        onError: () => toast.error(t('EXISTING_LOANS.TOASTS.ERROR.CHANGE')),
        onSuccess: ({ position }) => {
            toast.success(t('EXISTING_LOANS.TOASTS.SUCCESS.CHANGE'));
            setQueryPosition(JSON.stringify(queryKeys.get(loanRequestId)), position);
        },
    });

    const addMutation = useMutation({
        mutationFn: mutationGenerator('add'),
        onError: () => toast.error(t('EXISTING_LOANS.TOASTS.ERROR.ADD')),
        onSuccess: ({ position }) => {
            toast.success(t('EXISTING_LOANS.TOASTS.SUCCESS.ADD'));
            setQueryPosition(JSON.stringify(queryKeys.get(loanRequestId)), position);
        },
    });

    const removeMutation = useMutation({
        mutationFn: mutationGenerator('remove'),
        onError: () => toast.error(t('EXISTING_LOANS.TOASTS.ERROR.REMOVE')),
        onSuccess: ({ position }) => {
            toast.success(t('EXISTING_LOANS.TOASTS.SUCCESS.REMOVE'));
            setQueryPosition(JSON.stringify(queryKeys.get(loanRequestId)), position);
        },
    });

    const switchLoanTypeMutation = useMutation({
        mutationFn: async ({
            newLoanType,
            oldLoanType,
            payload,
        }: {
            newLoanType: LoanType;
            oldLoanType: LoanType;
            payload: CreditMutationPayload;
        }) => {
            const { gehoertZuAntragsteller, id } = payload;
            if (id == null || gehoertZuAntragsteller == null) {
                throw new Error('id and gehoertZuAntragsteller are required for switching loan type');
            }

            await mutationGenerator('remove')({
                loanType: oldLoanType,
                payload: { gehoertZuAntragsteller, id },
            });

            return mutationGenerator('add')({
                loanType: newLoanType,
                payload,
            });
        },
        onError: () => toast.error(t('EXISTING_LOANS.TOASTS.ERROR.CHANGE')),
        onSuccess: ({ position }) => {
            toast.success(t('EXISTING_LOANS.TOASTS.SUCCESS.CHANGE'));
            setQueryPosition(JSON.stringify(queryKeys.get(loanRequestId)), position);
        },
    });

    return { addMutation, changeMutation, removeMutation, switchLoanTypeMutation };
};
